import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { ProposalNodeSet } from '../../types';
import {
  defaultStoreyStructureMap,
  type ParsedHardwareCsvItem,
} from '../../utils/proposalHardwareCsvParser';
import './TimberListUpdateModal.css';
import './HardwareListApplyModal.css';

interface HardwareListApplyModalProps {
  fileName: string;
  storeys: string[];
  items: ParsedHardwareCsvItem[];
  nodeSets: ProposalNodeSet[];
  onCancel: () => void;
  onApply: (storeyToNodeSetId: Record<string, string>) => void;
}

const HardwareListApplyModal: React.FC<HardwareListApplyModalProps> = ({
  fileName,
  storeys,
  items,
  nodeSets,
  onCancel,
  onApply,
}) => {
  const [map, setMap] = useState<Record<string, string>>(() =>
    defaultStoreyStructureMap(storeys, nodeSets),
  );

  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const storey of storeys) out[storey] = 0;
    for (const item of items) {
      const tokens = (item.storey || '').split(/\s+/).filter(Boolean);
      const used = tokens.length ? tokens : storeys;
      for (const token of used) {
        if (out[token] != null) out[token] += 1;
      }
    }
    return out;
  }, [items, storeys]);

  const allMapped = storeys.every((s) => !!map[s]);

  return (
    <div className="tlu-overlay" role="dialog" aria-modal="true" aria-labelledby="hla-title">
      <div className="tlu-modal hla-modal">
        <div className="tlu-header">
          <div className="tlu-header-text">
            <h2 id="hla-title">Apply hardware list</h2>
            <p>
              Map each storey in <strong>{fileName}</strong> to a structure. Shared tags
              (for example 5.00 5.01 5.02) copy onto every mapped structure.
            </p>
          </div>
          <button type="button" className="tlu-close" onClick={onCancel} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="hla-body">
          {nodeSets.length === 0 && (
            <p className="hla-empty">Add a structure on this proposal first, then map each storey to it.</p>
          )}
          {storeys.length === 0 && (
            <p className="hla-empty">No storey tags found. All rows will land on the first structure.</p>
          )}
          {storeys.map((storey) => (
            <label key={storey} className="hla-row">
              <span className="hla-storey">
                Storey {storey}
                <small>{counts[storey] || 0} part{counts[storey] === 1 ? '' : 's'}</small>
              </span>
              <select
                value={map[storey] || ''}
                onChange={(e) => setMap((prev) => ({ ...prev, [storey]: e.target.value }))}
              >
                <option value="">Choose structure…</option>
                {nodeSets.map((ns) => (
                  <option key={ns.id} value={ns.id}>
                    {ns.name}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
        <div className="tlu-footer">
          <button type="button" className="tlu-btn tlu-btn--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="tlu-btn tlu-btn--primary"
            disabled={nodeSets.length === 0 || (!allMapped && storeys.length > 0)}
            onClick={() => onApply(map)}
          >
            Apply to structures
          </button>
        </div>
      </div>
    </div>
  );
};

export default HardwareListApplyModal;
